import styled, { keyframes, css } from "styled-components";
import { Form, Modal, Row, Col } from "react-bootstrap";
import CustomButton from "../custom-button";
import { tokens } from "./tokens";

// ===================== ANIMAÇÕES =====================

export const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -48%) scale(0.97);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;

export const slideLeft = keyframes`
  from { opacity: 0; transform: translateX(30px); }
  to { opacity: 1; transform: translateX(0); }
`;

export const slideRight = keyframes`
  from { opacity: 0; transform: translateX(-30px); }
  to { opacity: 1; transform: translateX(0); }
`;

export const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
  20%, 40%, 60%, 80% { transform: translateX(4px); }
`;

export const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`;

export const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

// ===================== MODAL PRINCIPAL =====================

export const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: ${tokens.zModal - 1};
  animation: ${fadeIn} 0.2s ease;
`;

export const StyledModal = styled(Modal)`
  top: 50%;
  left: 50%;
  overflow: hidden;
  max-width: 95%;
  position: fixed;
  border-radius: ${tokens.radiusXl};
  font-family: ${tokens.fontFamily};
  transform: translate(-50%, -50%);
  z-index: ${tokens.zModal};
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  transition: none;
  box-shadow: ${tokens.shadowLg};

  @media (max-width: ${tokens.breakpointMd}) {
    max-width: 100%;
    width: 95%;
    margin: 0;
    max-height: 95vh;
    border-radius: ${tokens.radiusLg};
  }

  @media (max-width: ${tokens.breakpointSm}) {
    width: 100%;
    max-width: 100%;
    max-height: 100vh;
    border-radius: 0;
    top: 0;
    left: 0;
    transform: none;
  }
`;

export const ModalContentWrapper = styled(Modal.Body)`
  border-radius: ${tokens.radiusXl};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 90vh;

  @media (max-width: ${tokens.breakpointSm}) {
    max-height: 100vh;
    border-radius: 0;
  }
`;

// ===================== HEADER =====================

export const StyledModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${tokens.spacingLg} ${tokens.spacingXl};
  border-bottom: 1px solid ${tokens.borderLight};
  background: ${tokens.bgWhite};
  flex-shrink: 0;

  @media (max-width: ${tokens.breakpointMd}) {
    padding: ${tokens.spacingMd} ${tokens.spacingLg};
  }
`;

export const ModalTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${tokens.spacingMd};
  flex-wrap: wrap;
  min-width: 0;
  flex: 1;
`;

export const ModalTitle = styled.h5`
  font-weight: 600;
  font-size: ${tokens.fontXxl};
  color: ${tokens.textPrimary};
  margin: 0;
  line-height: 1.3;

  @media (max-width: ${tokens.breakpointMd}) {
    font-size: ${tokens.fontLg};
  }
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${tokens.spacingSm};
  flex-shrink: 0;
`;

export const CloseHeaderButton = styled.button`
  background: none;
  border: none;
  color: ${tokens.textMuted};
  font-size: 1.3rem;
  cursor: pointer;
  padding: ${tokens.spacingXs};
  border-radius: ${tokens.radiusMd};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${tokens.transitionBase};
  width: 32px;
  height: 32px;

  &:hover {
    background: ${tokens.bgLight};
    color: ${tokens.textPrimary};
  }

  &:focus {
    outline: 2px solid ${tokens.borderFocus};
    outline-offset: 2px;
  }
`;

export const TutorialVideoOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 32px;
  animation: ${fadeIn} 0.2s ease;
`;

export const TutorialVideoContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 1100px;
  background: #0b0b0b;
  border-radius: 12px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.5);
  overflow: hidden;
`;

export const TutorialVideoHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: linear-gradient(180deg, #15367E 0%, #1E47A8 100%);
  color: #fff;
`;

export const TutorialVideoTitle = styled.h3`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;

export const TutorialVideoExternalLink = styled.a`
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: #fff;
  height: 36px;
  padding: 0 14px;
  border-radius: 18px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  transition: all ${tokens.transitionBase};

  &:hover {
    background: rgba(255, 255, 255, 0.28);
    color: #fff;
    text-decoration: none;
  }

  &:focus {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }
`;

export const TutorialVideoHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const TutorialVideoClose = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: #fff;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  font-size: 1.1rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all ${tokens.transitionBase};

  &:hover {
    background: rgba(255, 255, 255, 0.28);
    transform: scale(1.05);
  }

  &:focus {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }
`;

export const TutorialVideoBody = styled.div`
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  background: #000;
`;

export const TutorialVideoIframe = styled.iframe`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
`;

export const TutorialIcon = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: #1E47A8;
  border: none;
  border-radius: 6px;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  transition: all ${tokens.transitionBase};
  margin-left: 0.3rem;
  white-space: nowrap;

  svg {
    flex-shrink: 0;
  }

  &:hover {
    background: #15367E;
    color: #fff;
  }

  &:focus {
    outline: 2px solid ${tokens.borderFocus};
    outline-offset: 2px;
  }
`;

// ===================== BODY =====================

export const StyledModalBody = styled.div<{ $alto?: boolean }>`
  margin-bottom: 0;
  padding: ${tokens.spacingLg};
  padding-bottom: 0;
  max-height: calc(70vh - 180px);
  ${p => p.$alto && `
    /* Ocupa o que sobra entre etapas e rodapé, sem empurrar o rodapé para fora do modal. */
    max-height: none !important;
    flex: 1 1 auto;
    min-height: 0;
    padding-bottom: ${tokens.spacingMd};
  `}
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: ${tokens.bgScrollThumb} transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    border-radius: ${tokens.radiusSm};
    margin: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${tokens.bgScrollThumb};
    border-radius: ${tokens.radiusSm};
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${tokens.bgScrollThumbHover};
  }

  @media (max-width: ${tokens.breakpointLg}) {
    max-height: calc(65vh - 160px);
  }

  @media (max-width: ${tokens.breakpointMd}) {
    max-height: calc(60vh - 100px);
    padding: ${tokens.spacingSm};
  }

  @media (max-width: ${tokens.breakpointSm}) {
    max-height: calc(100vh - 180px);
    padding: ${tokens.spacingMd};
  }
`;

export const StyledForm = styled(Form)<{ $alto?: boolean }>`
  background: ${tokens.bgWhite};
  display: flex;
  flex-direction: column;
  height: 100%;
  ${p => p.$alto && `
    /* Limita o formulário ao espaço útil do modal (90vh menos as margens do diálogo e o padding do wrapper),
       para o corpo encolher e o rodapé ficar inteiro à vista. */
    min-height: 0;
    max-height: calc(90vh - 96px);
  `}
`;

// ===================== GRID =====================

export const StyledRow = styled(Row)`
  margin-bottom: 0.2rem;
  align-items: flex-start;

  @media (max-width: ${tokens.breakpointMd}) {
    margin-bottom: ${tokens.spacingSm};
  }
`;

export const StyledCol = styled(Col)`
  padding-left: ${tokens.spacingMd};
  padding-right: ${tokens.spacingMd};

  @media (max-width: ${tokens.breakpointLg}) {
    padding-left: ${tokens.spacingSm};
    padding-right: ${tokens.spacingSm};
  }

  @media (max-width: ${tokens.breakpointMd}) {
    padding-left: ${tokens.spacingXs};
    padding-right: ${tokens.spacingXs};
    margin-bottom: ${tokens.spacingMd};
  }
`;

// ===================== FORM CONTROLS =====================

export const StyledFormControl = styled(Form.Control)<{ hasError?: boolean }>`
  font-size: ${tokens.fontBase};
  border-radius: ${tokens.radiusMd};
  border: 1.5px solid ${props => props.hasError ? tokens.errorColor : tokens.borderDefault};
  transition: border-color ${tokens.transitionFast}, box-shadow ${tokens.transitionFast};
  padding: 0.5rem 0.75rem;

  &:focus {
    border-color: ${props => props.hasError ? tokens.errorColor : tokens.primaryColor};
    box-shadow: 0 0 0 3px rgba(${props => props.hasError ? '220, 53, 69' : '31, 70, 126'}, 0.15);
  }

  &:disabled, &[readonly] {
    background-color: ${tokens.bgLight};
    cursor: not-allowed;
    opacity: 0.7;
  }

  @media (max-width: ${tokens.breakpointMd}) {
    padding: ${tokens.spacingSm};
    font-size: ${tokens.fontMd};
  }
`;

export const StyledFormGroup = styled(Form.Group)<{ $hasError?: boolean }>`
  margin-bottom: 1.2rem;
  position: relative;

  @media (max-width: ${tokens.breakpointMd}) {
    margin-bottom: ${tokens.spacingMd};
  }
`;

// ===================== LABELS =====================

export const StyledLabelTitle = styled(Form.Label)<{ bold?: boolean; required?: boolean }>`
  font-size: ${tokens.fontXl};
  color: ${tokens.textSecondary};
  font-weight: ${props => props.bold ? "700" : "600"};
  margin-bottom: 0;
  display: flex;
  align-items: center;
  white-space: nowrap;

  &::after {
    content: ${props => props.required ? "'*'" : "''"};
    color: ${tokens.errorColor};
    margin-left: 4px;
  }

  @media (max-width: ${tokens.breakpointMd}) {
    font-size: ${tokens.fontMd};
  }
`;

export const StyledLabel = styled(Form.Label)<{ bold?: boolean; required?: boolean }>`
  font-size: ${tokens.fontLg};
  color: ${tokens.textSecondary};
  font-weight: ${props => props.bold ? "700" : "600"};
  margin-bottom: 0;
  display: flex;
  align-items: center;
  white-space: nowrap;

  &::after {
    content: ${props => props.required ? "'*'" : "''"};
    color: ${tokens.errorColor};
    margin-left: 4px;
  }

  @media (max-width: ${tokens.breakpointMd}) {
    font-size: ${tokens.fontMd};
  }
`;

export const StyledSubLabel = styled(Form.Label)<{ bold?: boolean; required?: boolean }>`
  font-size: ${tokens.fontBase};
  color: ${tokens.textHint};
  font-weight: 400;
  margin-bottom: 0;
  padding-bottom: 8px;
  display: flex;
  align-items: center;
  white-space: normal;

  &::after {
    content: ${props => props.required ? "'*'" : "''"};
    color: ${tokens.errorColor};
    margin-left: 4px;
  }

  @media (max-width: ${tokens.breakpointMd}) {
    font-size: ${tokens.fontMd};
  }
`;

export const ErrorMessage = styled.div`
  color: ${tokens.errorColor};
  font-size: ${tokens.fontXs};
  font-weight: 400;
  position: absolute;
  margin-top: 0.10rem;

  @media (max-width: ${tokens.breakpointMd}) {
    font-size: 0.8rem;
    position: relative;
  }
`;

export const CampoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.1rem;
  flex-wrap: wrap;
  margin-bottom: 0.3rem;
`;

export const AjudinhaWrapper = styled.div`
  margin-bottom: 0.05rem;
`;

export const AjudinhaWrapperCheckbox = styled.div`
  margin-left: 0.1rem;
  margin-bottom: -20px;
`;

// ===================== BOTÕES =====================

interface PrimaryButtonProps {
  background?: string;
  hoverBackground?: string;
}

export const PrimaryButton = styled(CustomButton)<PrimaryButtonProps>`
  background: ${({ background }) => background || tokens.primaryGradient};
  border: none;
  color: white;
  border-radius: ${tokens.radiusSm};
  transition: all ${tokens.transitionSlow};
  font-weight: 500;
  position: relative;
  overflow: hidden;

  &:hover {
    background: ${({ hoverBackground, background }) => hoverBackground || background || tokens.primaryGradientHover};
    box-shadow: ${tokens.shadowSm};
    transform: translateY(-1px);
  }

  &:active {
    background: ${({ background }) => background || tokens.primaryGradient};
    transform: translateY(0);
  }

  &:disabled {
    background: ${tokens.bgDisabled};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  &:focus-visible {
    outline: 2px solid ${tokens.borderFocus};
    outline-offset: 2px;
  }
`;

export const SecondaryButton = styled(CustomButton)`
  background: transparent;
  color: ${tokens.textSecondary};
  border: none;
  transition: all ${tokens.transitionBase};
  font-weight: 500;

  &:hover {
    background: rgba(108, 117, 125, 0.1);
    color: ${tokens.textPrimary};
  }

  &:active {
    background: rgba(108, 117, 125, 0.2);
  }

  &:focus-visible {
    outline: 2px solid ${tokens.borderFocus};
    outline-offset: 2px;
  }
`;

export const ExtraButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${tokens.textSecondary};
  font-size: ${tokens.fontMd};
  display: flex;
  align-items: center;
  padding-left: 0;

  &:hover {
    color: #626466;
  }

  &:focus {
    outline: none;
  }
`;

// ===================== FOOTER =====================

export const StyledModalFooter = styled.div`
  padding: ${tokens.spacingMd} ${tokens.spacingLg};
  border-top: 1px solid ${tokens.borderLight};
  border-bottom-left-radius: ${tokens.radiusXl};
  border-bottom-right-radius: ${tokens.radiusXl};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${tokens.spacingMd};
  justify-content: space-between;
  width: 100%;
  z-index: ${tokens.zFooter};
  margin-top: auto;
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${tokens.bgWhite};
  flex-shrink: 0;

  @media (max-width: ${tokens.breakpointMd}) {
    flex-direction: column;
    gap: ${tokens.spacingSm};
    padding: ${tokens.spacingLg};
    position: sticky;
    bottom: 0;
  }

  @media (max-width: ${tokens.breakpointSm}) {
    padding: ${tokens.spacingMd};
    gap: 0.4rem;
    border-radius: 0;
  }
`;

export const FooterButtonsContainer = styled.div`
  margin-left: auto;
  display: flex;
  gap: ${tokens.spacingMd};
  flex-shrink: 0;
  align-items: center;

  @media (max-width: ${tokens.breakpointMd}) {
    width: 100%;
    justify-content: flex-end;
  }

  @media (max-width: ${tokens.breakpointSm}) {
    flex-direction: column-reverse;
    width: 100%;

    > button, > * {
      width: 100%;
    }
  }
`;

export const FooterTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: ${tokens.spacingMd};
  align-items: flex-start;
  flex-grow: 1;

  @media (max-width: ${tokens.breakpointMd}) {
    padding-left: 0;
    width: 100%;
    align-items: center;
  }
`;

/**
 * Área do rodapé para controles livres (ver footerContent em PropsModalForm). Ocupa o espaço à
 * esquerda dos botões e, no celular, vira uma faixa própria acima deles em vez de espremer o "Salvar".
 */
export const FooterSlot = styled.div`
  display: flex;
  align-items: center;
  padding-left: ${tokens.spacingMd};
  flex-grow: 1;
  min-width: 0;

  @media (max-width: ${tokens.breakpointMd}) {
    padding-left: 0;
    width: 100%;
    justify-content: center;
    flex-grow: 0;
  }
`;

export const FooterText = styled.p<{ color?: string; bold?: boolean }>`
  margin: 0;
  font-size: ${tokens.fontBase};
  color: ${props => props.color || tokens.textSecondary};
  font-weight: ${props => props.bold === true ? '700' : '400'};

  @media (max-width: ${tokens.breakpointMd}) {
    font-size: ${tokens.fontMd};
    text-align: center;
  }
`;

export const KeyboardHint = styled.span`
  font-size: ${tokens.fontXs};
  color: ${tokens.textMuted};
  display: flex;
  align-items: center;
  gap: 0.3rem;

  kbd {
    background: ${tokens.bgLight};
    border: 1px solid ${tokens.borderDefault};
    border-radius: 3px;
    padding: 0.1rem 0.35rem;
    font-size: ${tokens.fontXs};
    font-family: ${tokens.fontFamily};
    color: ${tokens.textSecondary};
    box-shadow: 0 1px 0 ${tokens.borderDefault};
  }

  @media (max-width: ${tokens.breakpointMd}) {
    display: none;
  }
`;

// ===================== PROGRESS STEPS =====================

export const ProgressContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: ${tokens.spacingLg} ${tokens.spacingSm};
  background: ${tokens.bgWhite};
  border-bottom: 1px solid ${tokens.borderLight};
  overflow-x: auto;
  scrollbar-width: none;
  flex-shrink: 0;
  gap: 0;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: ${tokens.breakpointMd}) {
    padding: ${tokens.spacingSm};
  }
`;

export const ProgressStep = styled.div<{ active: boolean; completed: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1 1 0;
  min-width: 0;
  position: relative;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 16px;
    left: calc(50% + 1rem);
    width: calc(100% - 2rem);
    height: 2px;
    background: ${props => props.completed ? tokens.primaryColor : '#E0E0E0'};
    transform: translateY(-50%);
    z-index: 0;
    transition: background ${tokens.transitionSlow};
  }
`;

export const StepCircle = styled.div<{ active: boolean; completed: boolean }>`
  width: 2rem;
  height: 2rem;
  border-radius: ${tokens.radiusFull};
  border: 2px solid ${props => (props.active || props.completed) ? tokens.primaryColor : '#E0E0E0'};
  background: ${props => props.active ? tokens.primaryColor : tokens.bgWhite};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.active ? tokens.bgWhite : (props.completed ? tokens.primaryColor : tokens.textMuted)};
  font-size: ${tokens.fontSm};
  font-weight: 600;
  z-index: 1;
  position: relative;
  transition: all ${tokens.transitionSlow};
  flex-shrink: 0;

  ${props => props.completed && !props.active && `
    &::before {
      content: '\\2713';
      color: ${tokens.primaryColor};
      font-size: 0.85rem;
    }
  `}

  @media (max-width: ${tokens.breakpointMd}) {
    width: 1.75rem;
    height: 1.75rem;
    font-size: ${tokens.fontXs};
  }
`;

export const StepTitle = styled.span<{ active: boolean; completed: boolean }>`
  font-size: 0.7rem;
  font-weight: ${props => props.active ? '600' : '500'};
  color: ${props => props.active || props.completed ? tokens.primaryColor : tokens.textMuted};
  text-align: center;
  margin-top: 0.2rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  transition: color ${tokens.transitionBase};

  @media (max-width: ${tokens.breakpointMd}) {
    display: none;
  }
`;

// Etapas numa linha só (corpoAlto): círculo pequeno e título ao lado, para sobrar espaço ao conteúdo.
export const PassosCompactos = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px ${tokens.spacingLg};
  border-bottom: 1px solid ${tokens.borderLight};
  flex-shrink: 0;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const PassoCompacto = styled.div<{ active: boolean; completed: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  font-size: 0.8rem;
  font-weight: ${p => (p.active ? 600 : 500)};
  color: ${p => (p.active || p.completed ? tokens.primaryColor : tokens.textMuted)};

  span {
    width: 1.35rem;
    height: 1.35rem;
    border-radius: ${tokens.radiusFull};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.7rem;
    border: 1.5px solid ${p => (p.active || p.completed ? tokens.primaryColor : '#D0D5DB')};
    background: ${p => (p.active ? tokens.primaryColor : tokens.bgWhite)};
    color: ${p => (p.active ? tokens.bgWhite : 'inherit')};
  }
`;

export const SeparadorPasso = styled.div<{ completed: boolean }>`
  flex: 1 1 24px;
  min-width: 16px;
  height: 2px;
  background: ${p => (p.completed ? tokens.primaryColor : '#E0E0E0')};
`;

// ===================== FILE/IMAGE/VIDEO UPLOAD =====================

export const FileUploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${tokens.spacingXxl};
  border: 2px dashed ${tokens.borderDefault};
  border-radius: ${tokens.radiusLg};
  background-color: ${tokens.bgLight};
  cursor: pointer;
  transition: all ${tokens.transitionSlow};
  margin-top: ${tokens.spacingLg};
  text-align: center;

  &:hover {
    border-color: ${tokens.borderFocus};
    background-color: ${tokens.primaryLight};
  }

  &.active {
    border-color: ${tokens.primaryColor};
    background-color: ${tokens.primaryLight};
    transform: scale(1.01);
  }
`;

export const VideoUploadContainer = styled(FileUploadContainer)`
  margin-top: 0;
`;

export const UploadIcon = styled.div`
  font-size: 2.5rem;
  color: ${tokens.textMuted};
  margin-bottom: ${tokens.spacingLg};
`;

export const UploadText = styled.p`
  margin: 0;
  color: ${tokens.textMuted};
  font-size: ${tokens.fontBase};
`;

export const FileInput = styled.input`
  display: none;
`;

export const FilePreviewContainer = styled.div`
  position: relative;
  display: inline-block;
  margin-top: ${tokens.spacingLg};
  width: 100%;
  min-height: 60px;
`;

export const FilePreview = styled.div<{ empty?: boolean }>`
  display: flex;
  align-items: center;
  padding: ${tokens.spacingMd};
  background-color: ${tokens.bgLight};
  border-radius: ${tokens.radiusMd};
  margin-top: ${tokens.spacingSm};
  width: 100%;
  border: 1px solid ${tokens.borderLight};
  min-height: 60px;
  visibility: ${props => props.empty ? 'hidden' : 'visible'};
  transition: box-shadow ${tokens.transitionBase};

  &:hover {
    box-shadow: ${tokens.shadowSm};
  }
`;

export const FileIcon = styled.div`
  margin-right: ${tokens.spacingMd};
  font-size: 1.5rem;
  color: ${tokens.textMuted};
`;

export const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export const FileName = styled.p`
  margin: 0;
  font-size: ${tokens.fontMd};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const FileSize = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: ${tokens.textMuted};
`;

export const FileActions = styled.div`
  display: flex;
  gap: ${tokens.spacingSm};
`;

export const FileActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: ${tokens.fontLg};
  padding: 0 ${tokens.spacingSm};
  transition: all ${tokens.transitionBase};
  color: ${tokens.textMuted};
  border-radius: ${tokens.radiusSm};

  &:hover {
    transform: scale(1.1);
    color: ${tokens.primaryColor};
  }

  &:focus-visible {
    outline: 2px solid ${tokens.borderFocus};
    outline-offset: 2px;
  }
`;

// ===================== IMAGE PREVIEW OVERLAY =====================

export const ImagePreviewOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${tokens.bgImageOverlay};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${tokens.zImagePreview};
  padding: 20px;
  animation: ${fadeIn} 0.2s ease;
`;

export const ImagePreviewContent = styled.div`
  position: relative;
  max-width: 90%;
  max-height: 90%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 80vh;
  border-radius: ${tokens.radiusLg};
  box-shadow: ${tokens.shadowImage};
`;

export const CloseButton = styled.button`
  position: absolute;
  top: -40px;
  right: -40px;
  background: none;
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
  z-index: ${tokens.zImagePreview + 1};
  transition: transform ${tokens.transitionBase};

  &:hover {
    transform: scale(1.2);
  }

  @media (max-width: ${tokens.breakpointMd}) {
    top: -30px;
    right: 0;
  }
`;

export const DownloadButton = styled.button`
  position: absolute;
  bottom: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: ${tokens.radiusFull};
  width: 50px;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all ${tokens.transitionSlow};
  z-index: ${tokens.zImagePreview + 1};

  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.1);
  }

  @media (max-width: ${tokens.breakpointMd}) {
    width: 40px;
    height: 40px;
    bottom: 10px;
    right: 10px;
  }
`;

// ===================== VIDEO =====================

export const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  margin-top: ${tokens.spacingLg};
`;

export const VideoPreview = styled.video`
  width: 100%;
  max-height: 400px;
  border-radius: ${tokens.radiusLg};
  background-color: #000;
`;

export const VideoControls = styled.div`
  display: flex;
  gap: ${tokens.spacingSm};
  margin-top: ${tokens.spacingSm};
  justify-content: center;
  flex-wrap: wrap;
`;

export const VideoButton = styled.button<{ variant?: 'primary' | 'danger' | 'secondary' }>`
  background: ${props => {
    switch (props.variant) {
      case 'primary': return '#007bff';
      case 'danger': return tokens.errorColor;
      default: return tokens.textMuted;
    }
  }};
  color: white;
  border: none;
  padding: ${tokens.spacingSm} ${tokens.spacingLg};
  border-radius: ${tokens.radiusSm};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${tokens.spacingSm};
  font-size: ${tokens.fontMd};
  transition: all ${tokens.transitionBase};

  &:hover {
    filter: brightness(0.9);
    transform: translateY(-1px);
  }

  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
    transform: none;
  }
`;

// ===================== LOADING / SKELETON =====================

export const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${tokens.bgOverlay};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${tokens.zOverlay};
  border-radius: ${tokens.radiusXl};
`;

export const SkeletonLine = styled.div<{ width?: string; height?: string }>`
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
  border-radius: ${tokens.radiusSm};
  width: ${props => props.width || '100%'};
  height: ${props => props.height || '16px'};
`;

export const SkeletonField = styled.div`
  margin-bottom: 1.2rem;
`;

export const SkeletonFieldRow = styled.div`
  display: flex;
  gap: ${tokens.spacingLg};
  margin-bottom: ${tokens.spacingLg};

  @media (max-width: ${tokens.breakpointMd}) {
    flex-direction: column;
  }
`;

export const SkeletonFieldGroup = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacingSm};
`;

// ===================== INDICADORES =====================

export const NovoIndicator = styled.span`
  background-color: ${tokens.successColor};
  color: white;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.15rem 0.4rem;
  border-radius: 10px;
  margin-left: ${tokens.spacingSm};
  vertical-align: middle;
`;

export const PremiumWarning = styled.div`
  color: ${tokens.warningColor};
  background-color: ${tokens.warningBg};
  padding: ${tokens.spacingSm};
  border-radius: ${tokens.radiusSm};
  font-size: ${tokens.fontSm};
  margin-top: ${tokens.spacingSm};
  display: flex;
  align-items: center;
  gap: ${tokens.spacingSm};
`;

export const UnsavedBadge = styled.span`
  background: ${tokens.warningColor};
  color: ${tokens.textPrimary};
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.15rem 0.5rem;
  border-radius: 10px;
  animation: ${pulse} 2s infinite;
`;

// ===================== EMBUTIDO (acoplado à página) =====================

/** A mesma janela do modal (tamanho lg), parada na página: sem fundo escuro e sem posição fixa. */
export const CartaoEmbutido = styled.div`
  position: relative;
  width: 800px;
  max-width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  background: ${tokens.bgWhite};
  border-radius: ${tokens.radiusXl};
  box-shadow: ${tokens.shadowLg};
  font-family: ${tokens.fontFamily};
  overflow: hidden;
`;
